import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/auth";

/**
 * ProtectedRoute — wraps any element that requires a signed-in user.
 *
 * - `loading` → show a lightweight spinner (i18n string, no layout shift).
 * - `anonymous` → redirect to `/login`, preserving the attempted path in
 *   location state so LoginPage can forward `redirect=<path>` to the backend.
 * - `authenticated` → render children.
 */
export default function ProtectedRoute({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { state } = useAuth();
  const location = useLocation();

  if (state.kind === "loading") {
    return (
      <div
        role="status"
        aria-live="polite"
        className="mx-auto mt-xl max-w-md p-md text-center text-neutral-700"
      >
        {t("auth.loading")}
      </div>
    );
  }

  if (state.kind === "anonymous") {
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to="/login" replace state={{ returnTo }} />;
  }

  return <>{children}</>;
}
